/**
 * "Monitoring" tab — boot diagnostics (managed storage / custom storage account / disabled).
 * Alerts, OS guest diagnostics and application health are shown disabled with the reason:
 * they are not supported by the portal yet and are never faked as working switches.
 */
import { useAzureCatalog } from '@/hooks/useAppriseAzure'
import { RadioField, ReadOnlyField } from './FormFields'
import type { AppriseBootDiagnosticsMode } from '@/types'
import { DisabledCheck, LiveSelectField } from './LiveSelect'
import styles from './AppriseCreateVm.module.css'
import type { AppriseVmFormApi } from './useAppriseVmForm'

export function AppriseMonitoringTab({ form }: { form: AppriseVmFormApi }) {
  const o = form.config.operations
  const boot = o.boot_diagnostics
  const err = form.validation.errors
  const accounts = useAzureCatalog('storage-accounts', { location: form.location }, form.enabled && boot.mode === 'custom')
  const current = boot.storage_account ? `${boot.storage_account_resource_group}/${boot.storage_account}` : ''
  return (
    <>
      <div className={styles.sectionTitle}>Alerts</div>
      <DisabledCheck label="Enable recommended alert rules" />

      <div className={styles.sectionTitle}>Diagnostics</div>
      <RadioField<AppriseBootDiagnosticsMode> name="ap_boot_mode" label="Boot diagnostics" value={boot.mode} full error={err['operations.boot_diagnostics.mode']}
        options={[
          { value: 'managed', label: 'Enable with managed storage account (recommended)' },
          { value: 'custom', label: 'Enable with custom storage account' },
          { value: 'disabled', label: 'Disable' },
        ]}
        onChange={(v) => form.setBootDiagnostics({ mode: v })} />
      {boot.mode === 'custom' && (
        <LiveSelectField label="Diagnostic storage account" required full catalog={accounts} value={current}
          emptyText="No storage accounts found in this region" valueLabel={boot.storage_account ? `${boot.storage_account} (${boot.storage_account_resource_group})` : undefined}
          error={err['operations.boot_diagnostics.storage_account'] ?? err['operations.boot_diagnostics.storage_account_resource_group']}
          placeholder="Choose one…" options={accounts.items.map((a) => ({ value: `${a.resource_group}/${a.name}`, label: `${a.name} (${a.resource_group}) — ${a.sku}` }))}
          onChange={(v) => {
            const hit = accounts.items.find((a) => `${a.resource_group}/${a.name}` === v)
            if (hit) form.network.bootStorage({ name: hit.name, resource_group: hit.resource_group })
          }} />
      )}
      <DisabledCheck label="Enable OS guest diagnostics" />
      <ReadOnlyField label="Azure Monitor agent + Log Analytics" value={o.monitoring_enabled ? 'On' : 'Off'}
        hint="Set by the platform catalog for this SKU — not editable here" />

      <div className={styles.sectionTitle}>Health</div>
      <DisabledCheck label="Enable application health monitoring" />
    </>
  )
}
